import { computed, onBeforeUnmount, onMounted, ref, toValue, watch } from 'vue'

import { liveEdgeTaperForBarIndex, liveScrollOffsetPx, resampleForBars, resampleLiveBars } from '../utils/waveform-peaks'

import type { UseVoiceWaveformCanvasOptions } from '../types/voice.types'

/** Canvas resize, live scroll, and bar paint for VoiceWaveform. */
export function useVoiceWaveformCanvas(options: UseVoiceWaveformCanvasOptions) {
  const canvasRef = ref<HTMLCanvasElement | null>(null)
  const widthPx = ref(0)
  const scrollProgress = ref(1)
  let resizeObserver: ResizeObserver | null = null
  let scrollRaf: number | null = null
  let lastSampleCount = 0
  let fillCache: { playedKey: string; unplayedKey: string; played: string; unplayed: string } | null = null

  const prefersReducedMotion = () =>
    typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches === true

  const barCount = computed(() => {
    const slot = toValue(options.barWidth) + toValue(options.barGap)
    if (widthPx.value <= 0 || slot <= 0) return 0
    return Math.max(1, Math.floor(widthPx.value / slot))
  })

  const bars = computed(() =>
    toValue(options.live)
      ? resampleLiveBars(toValue(options.samples), barCount.value)
      : resampleForBars(toValue(options.samples), barCount.value)
  )

  const clampedProgress = computed(() => Math.min(Math.max(toValue(options.progress), 0), 1))
  const ariaValueNow = computed(() => Math.round(clampedProgress.value * 100))

  onMounted(() => {
    measure()
    lastSampleCount = toValue(options.samples).length
    if (typeof ResizeObserver !== 'undefined' && canvasRef.value) {
      resizeObserver = new ResizeObserver(() => {
        measure()
        draw()
      })
      resizeObserver.observe(canvasRef.value)
    }
    draw()
  })

  onBeforeUnmount(() => {
    resizeObserver?.disconnect()
    resizeObserver = null
    cancelScrollRaf()
  })

  function cancelScrollRaf() {
    if (scrollRaf != null && typeof cancelAnimationFrame !== 'undefined') {
      cancelAnimationFrame(scrollRaf)
      scrollRaf = null
    }
  }

  function kickScrollTransition() {
    if (!toValue(options.live) || prefersReducedMotion()) {
      cancelScrollRaf()
      scrollProgress.value = 1
      return
    }
    cancelScrollRaf()
    scrollProgress.value = 0
    const started = typeof performance !== 'undefined' ? performance.now() : Date.now()
    const durationMs = 120

    const tick = (now: number) => {
      const t = Math.min(Math.max((now - started) / durationMs, 0), 1)
      scrollProgress.value = 1 - Math.pow(1 - t, 3)
      draw()
      if (t < 1) {
        scrollRaf = requestAnimationFrame(tick)
      } else {
        scrollRaf = null
      }
    }
    if (typeof requestAnimationFrame !== 'undefined') {
      scrollRaf = requestAnimationFrame(tick)
    } else {
      scrollProgress.value = 1
    }
  }

  function draw() {
    const canvas = canvasRef.value
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const barWidth = toValue(options.barWidth)
    const barGap = toValue(options.barGap)
    const height = toValue(options.height)
    const { played: playedColor, unplayed: unplayedColor } = resolveCachedFills(
      canvas,
      toValue(options.playedColor),
      toValue(options.unplayedColor)
    )
    const progress = toValue(options.progress)
    const live = toValue(options.live)

    const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1
    const cssWidth = widthPx.value
    const cssHeight = height
    const nextWidth = Math.max(1, Math.floor(cssWidth * dpr))
    const nextHeight = Math.max(1, Math.floor(cssHeight * dpr))
    if (canvas.width !== nextWidth || canvas.height !== nextHeight) {
      canvas.width = nextWidth
      canvas.height = nextHeight
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    ctx.clearRect(0, 0, cssWidth, cssHeight)

    const slot = barWidth + barGap
    const mid = cssHeight / 2
    const minBar = 2
    const radius = barWidth / 2
    const scrollX = live ? liveScrollOffsetPx(slot, scrollProgress.value) : 0

    const paintBars = (baseColor: string) => {
      bars.value.forEach((sample, index) => {
        const taper = live ? liveEdgeTaperForBarIndex(index, bars.value.length) : { scale: 1, opacity: 1 }
        const barHeight = Math.max(minBar, sample * cssHeight * taper.scale)
        const x = index * slot + scrollX
        const top = mid - barHeight / 2
        ctx.globalAlpha = taper.opacity
        ctx.fillStyle = baseColor
        ctx.beginPath()
        if (typeof ctx.roundRect === 'function') {
          ctx.roundRect(x, top, barWidth, barHeight, radius)
        } else {
          ctx.rect(x, top, barWidth, barHeight)
        }
        ctx.fill()
      })
      ctx.globalAlpha = 1
    }

    paintBars(unplayedColor)

    const playedWidth = Math.min(Math.max(progress, 0), 1) * cssWidth
    if (playedWidth > 0) {
      ctx.save()
      ctx.beginPath()
      ctx.rect(0, 0, playedWidth, cssHeight)
      ctx.clip()
      paintBars(playedColor)
      ctx.restore()
    }
  }

  function measure() {
    const canvas = canvasRef.value
    if (!canvas) return
    const rectWidth = canvas.getBoundingClientRect().width
    widthPx.value = rectWidth || canvas.clientWidth || canvas.parentElement?.clientWidth || 0
  }

  function onPointer(event: PointerEvent) {
    if (!toValue(options.seekable)) return
    const canvas = canvasRef.value
    if (!canvas) return
    const rect = canvas.getBoundingClientRect()
    if (rect.width <= 0) return
    const fraction = (event.clientX - rect.left) / rect.width
    options.onSeek(Math.min(Math.max(fraction, 0), 1))
  }

  function resolveCachedFills(el: HTMLCanvasElement, playedKey: string, unplayedKey: string) {
    if (fillCache && fillCache.playedKey === playedKey && fillCache.unplayedKey === unplayedKey) {
      return fillCache
    }
    fillCache = {
      playedKey,
      unplayedKey,
      played: resolveCanvasFillStyle(el, playedKey),
      unplayed: resolveCanvasFillStyle(el, unplayedKey),
    }
    return fillCache
  }

  watch(
    [
      () => toValue(options.samples),
      () => toValue(options.progress),
      () => toValue(options.live),
      barCount,
      () => toValue(options.playedColor),
      () => toValue(options.unplayedColor),
    ],
    () => {
      const samples = toValue(options.samples)
      if (toValue(options.live) && samples.length > lastSampleCount) {
        kickScrollTransition()
      } else {
        draw()
      }
      lastSampleCount = samples.length
    }
  )

  return {
    canvasRef,
    ariaValueNow,
    onPointer,
  }
}

/** Canvas fillStyle cannot paint `var(--token)`; resolve against the canvas theme. */
function resolveCanvasFillStyle(el: HTMLCanvasElement, color: string): string {
  if (!color) return color
  if (color === 'currentColor') {
    return getComputedStyle(el).color || color
  }
  if (!color.includes('var(')) return color

  const probe = document.createElement('span')
  probe.style.color = color
  el.appendChild(probe)
  const resolved = getComputedStyle(probe).color
  el.removeChild(probe)
  if (!resolved || resolved === 'rgba(0, 0, 0, 0)' || resolved === 'transparent') {
    return color
  }
  return resolved
}
